import React, { Component } from "react";
import ReactDOM from "react-dom";
import { Dialog } from "primereact/dialog";

/**
 * The two overlays of the standard set. Both are presentation only: they own
 * no state and run no logic, so whatever is put inside keeps its own
 * behaviour exactly as it was.
 *
 * Their look lives in assets/css/_ui.scss -- a dialog that scales in under a
 * gradient header, and a panel that floats inset from the edges.
 */

/**
 * A dialog. `visible` and `onHide` belong to whoever opens it.
 *
 *   <Modal visible={open} onHide={close} title="تعديل المرحلة"
 *          footer={<><button className="ui-btn">حفظ</button></>}>
 *     ...
 *   </Modal>
 */
export const Modal = ({ visible, onHide, title, footer, width = 520, children }) => (
  <Dialog
    visible={visible}
    onHide={onHide}
    header={title}
    footer={footer}
    dismissableMask
    draggable={false}
    style={{ width: `min(${width}px, 94vw)` }}
  >
    {children}
  </Dialog>
);

/**
 * The last question before something happens that cannot be taken back. One
 * shape for the whole product: a pulsing ring, the name of the thing in the
 * question, and two buttons of equal width with cancel on the quiet side.
 *
 *   <Confirm
 *     visible={!!doomed}
 *     onHide={() => setDoomed(null)}
 *     name={doomed?.name}
 *     note="المرحلة دي عليها 3 مواد."
 *     onConfirm={() => remove(doomed.id)}
 *   />
 *
 * Not every such question is a delete. `tone="warn"` paints it gold instead
 * of red, and `title`, `icon`, `confirmLabel` and `busyLabel` say what is
 * actually being asked:
 *
 *   <Confirm tone="warn" icon="pi pi-refresh"
 *            title="تعمل كلمة سر جديدة؟" confirmLabel="أيوه، اعملها"
 *            busyLabel="بنعمل كلمة سر…" … />
 */
export const Confirm = ({
  visible,
  onHide,
  name,
  note,
  title,
  icon,
  tone = "danger",
  confirmLabel = "أيوه، احذف",
  cancelLabel = "إلغاء",
  busy = false,
  busyLabel = "جاري الحذف...",
  onConfirm,
}) => (
  <Dialog
    visible={visible}
    onHide={onHide}
    showHeader={false}
    dismissableMask
    draggable={false}
    style={{ width: "min(420px, 94vw)" }}
  >
    <div className={tone === "warn" ? "ui-confirm is-bare is-warn" : "ui-confirm is-bare"}>
      {/* the spotlight: a soft glow behind an icon that lands and keeps
          sending one ring out, so the question cannot be missed */}
      <div className="ui-confirm-top">
        <span className="ui-confirm-ring" aria-hidden="true">
          <i className={icon || "pi pi-trash"}></i>
        </span>

        <b>{title || (name ? `تحذف «${name}»؟` : "تأكيد الحذف")}</b>
        <p>{note || "الحذف نهائي ومش هينفع ترجع عنه."}</p>
      </div>

      <div className="ui-confirm-row">
        <button
          type="button"
          className={tone === "warn" ? "ui-btn is-gold" : "ui-btn is-danger"}
          disabled={busy}
          onClick={onConfirm}
        >
          {busy ? <i className="pi pi-spin pi-spinner"></i> : null}
          {busy ? busyLabel : confirmLabel}
        </button>
        <button type="button" className="ui-btn is-secondary" onClick={onHide}>
          {cancelLabel}
        </button>
      </div>
    </div>
  </Dialog>
);

/**
 * A slide-over for the long forms, so a page is never left to open another
 * page just to add one row.
 *
 *   <Panel visible={open} onHide={close} title="إضافة حصة" subtitle="..."
 *          footer={<button className="ui-btn is-gold">حفظ</button>}>
 *     <AddLesson />
 *   </Panel>
 */
export class Panel extends Component {
  componentDidMount() {
    document.addEventListener("keydown", this.onKey);
  }

  componentWillUnmount() {
    document.removeEventListener("keydown", this.onKey);
  }

  onKey = (e) => {
    if (e.key === "Escape" && this.props.visible && this.props.onHide) this.props.onHide();
  };

  // a click on the dimmed area, but not one that started inside the panel
  onScrim = (e) => {
    if (e.target === e.currentTarget && this.props.onHide) this.props.onHide();
  };

  render() {
    const { visible, onHide, title, subtitle, footer, children } = this.props;
    if (!visible) return null;

    // The panel belongs to the card, not to the window: it is portalled into
    // the dashboard sheet, so it slides out of the card's own left edge and
    // the dimming stays inside its rounded corners instead of covering the
    // whole screen. Outside the dashboard (or before it mounts) it falls
    // back to the body, where the same rules make it full-screen.
    const host = document.querySelector(".dash-sheet") || document.body;

    return ReactDOM.createPortal(
      <div className="ui-panel-scrim" onMouseDown={this.onScrim}>
        {/* `wide` is for the forms that are really a workbench -- an exam
            and its questions, say -- where the usual width would leave
            every field in a column of its own */}
        <aside
          className={this.props.wide ? "ui-panel-card is-wide" : "ui-panel-card"}
          role="dialog"
          aria-modal="true"
          aria-label={title}
        >
          <div className="ui-panel-head">
            <span className="t">
              <b>{title}</b>
              {subtitle ? <small>{subtitle}</small> : null}
            </span>

            {/* Whatever is inside puts its primary action here, so the save
                stays in sight however long the form is. A form mounted in
                the panel portals its button into this slot (see AddLesson);
                a caller that owns the action itself passes `actions`. */}
            <span className="ui-panel-slot">{this.props.actions}</span>

            <button type="button" className="ui-panel-x" onClick={onHide} aria-label="إغلاق">
              <i className="pi pi-times"></i>
            </button>
          </div>

          <div className="ui-panel-body">{children}</div>

          {footer ? <div className="ui-panel-foot">{footer}</div> : null}
        </aside>
      </div>,
      host
    );
  }
}
